/* eslint-disable  */
// LowCreditsWarning.tsx
import React, { useState } from 'react';
import { Tooltip } from 'antd';
import { CallOutlined, SmsOutlined, ErrorOutlined } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { setCurrentPage } from '@/store/account/slice';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { useQuery } from '@tanstack/react-query';
import { getCredits } from '@/api/user';
import CreditsStatusModal from './CreditStatusModal';
import { getLoggedInUser } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'src/enums/ERoutes';
import { getResellerData } from '@/store/reseller/selector';

const LowCreditsWarning: React.FC<{ forReseller?: boolean }> = ({ forReseller = false }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const loggedInUser = forReseller
		? useAppSelector(getResellerData)
		: useAppSelector(getLoggedInUser);
	const [isLowCreditsModalVisible, setIsLowCreditsModalVisible] = useState(false);
	const [selectedCreditType, setSelectedCreditType] = useState<'voice' | 'sms' | 'both'>('both');

	// Fetch credit settings and user data
	const { data: creditDetails, isLoading } = useQuery({
		queryKey: ['getUserCredits'],
		queryFn: () => getCredits(loggedInUser?.userType as 'Client' | 'Reseller'),
		enabled: loggedInUser?.userType === 'Reseller' || loggedInUser?.userType === 'Client',
	});

	// Return null if loading or no data
	if (isLoading || !creditDetails?.data) {
		return null;
	}

	// Calculate credit values from query data
	const voiceCredits = parseFloat(
		((creditDetails.data.voiceFreeCredits || 0) + (creditDetails.data.voiceCredits || 0)).toFixed(
			2,
		),
	);
	const smsCredits = parseFloat(
		((creditDetails.data.smsFreeCredits || 0) + (creditDetails.data.smsCredits || 0)).toFixed(2),
	);

	const voiceAutoReloadEnabled = creditDetails.data.voiceAutoReloadCredits || false;
	const smsAutoReloadEnabled = creditDetails.data.smsAutoReloadCredits || false;

	// Only show if credits are low and auto-reload is off
	const hasLowVoiceCredits = voiceCredits <= 50 && !voiceAutoReloadEnabled;
	const hasLowSmsCredits = smsCredits <= 15 && !smsAutoReloadEnabled;

	// If no warning is needed, return null
	if (!hasLowVoiceCredits && !hasLowSmsCredits) {
		return null;
	}

	// Determine which type of credits are low
	const determineCreditType = () => {
		if (hasLowVoiceCredits && hasLowSmsCredits) {
			return 'both';
		}
		if (hasLowVoiceCredits) {
			return 'voice';
		}
		if (hasLowSmsCredits) {
			return 'sms';
		}
		return 'both';
	};

	// Handle credits warning click
	const handleCreditWarningClick = () => {
		setSelectedCreditType(determineCreditType());
		setIsLowCreditsModalVisible(true);
	};

	// Handle navigating to credits page
	const handleNavigateToCredits = () => {
		setIsLowCreditsModalVisible(false);
		if (loggedInUser?.userType === 'Reseller') {
			navigate(AppRoutes.RESELLER_MANAGE_CREDITS);
		} else {
			dispatch(setCurrentPage(ReceptionistDashboardSiderItem.CREDITS));
		}
	};

	// Handle modal close
	const handleModalClose = () => {
		setIsLowCreditsModalVisible(false);
	};

	return (
		<>
			<Tooltip
				title={
					<div className="text-xs">
						{hasLowVoiceCredits && (
							<p className="mb-1">
								Your voice credits are low and auto-reload is disabled. This may interrupt your
								voice calls.
							</p>
						)}
						{hasLowSmsCredits && (
							<p>
								Your SMS credits are low and auto-reload is disabled. This may prevent sending SMS
								messages.
							</p>
						)}
						<p className="mt-2 italic">Click to manage your credits</p>
					</div>
				}
				placement="right"
			>
				<div
					className="bg-white rounded-lg border border-orange-200 p-4 shadow-sm cursor-pointer hover:bg-orange-50 transition-all"
					onClick={handleCreditWarningClick}
				>
					<div className="flex mb-2">
						<ErrorOutlined className="text-orange-500 mr-2" style={{ fontSize: '18px' }} />
						<p className="text-gray-800 font-medium">Low credits alert</p>
					</div>

					<p className="text-sm text-gray-600 mb-3">Your service may be interrupted soon.</p>

					<div className="flex space-x-4">
						{hasLowVoiceCredits && (
							<div className="flex items-center">
								<CallOutlined className="text-blue-500 mr-1" style={{ fontSize: '16px' }} />
								<span className="text-sm font-medium">
									Voice: <span className="text-red-600">{voiceCredits}</span>
								</span>
							</div>
						)}

						{hasLowSmsCredits && (
							<div className="flex items-center">
								<SmsOutlined className="text-green-500 mr-1" style={{ fontSize: '16px' }} />
								<span className="text-sm font-medium">
									SMS: <span className="text-red-600">{smsCredits}</span>
								</span>
							</div>
						)}
					</div>

					<div className="text-xs text-blue-600 mt-2 text-right">Click to add credits →</div>
				</div>
			</Tooltip>

			<CreditsStatusModal
				isVisible={isLowCreditsModalVisible}
				onClose={handleModalClose}
				onNavigateToCredits={handleNavigateToCredits}
				voiceCredits={voiceCredits}
				smsCredits={smsCredits}
				voiceAutoReloadEnabled={voiceAutoReloadEnabled}
				smsAutoReloadEnabled={smsAutoReloadEnabled}
				selectedCreditType={selectedCreditType}
			/>
		</>
	);
};

export default LowCreditsWarning;
